"use client";

import type { TooltipRenderProps } from "react-joyride";
import {
  AlertAction,
  AlertDescription,
  AlertTitle,
  GuideCard,
  Icon,
  type GuideCardArrow,
} from "@focal/ui-kit";

/**
 * Tooltip for user guides (FE-1131, Figma 2856-26384 / 2856-26451),
 * rendered by react-joyride via `tooltipComponent`. Built on the ui-kit
 * GuideCard, which owns the palette, shadow, and beak — joyride's own
 * arrow is disabled in UserGuide.tsx (`floatingOptions.hideArrow`).
 */
export const UserGuideTooltip = ({
  index,
  isLastStep,
  size,
  step,
  primaryProps,
  closeProps,
  tooltipProps,
}: TooltipRenderProps) => {
  // GuideCard's Alert calls onDismiss with no arguments, but joyride's
  // close handler unconditionally calls event.preventDefault() — hand it
  // a stub so the closeButtonAction dispatch (skip → marked seen) stays
  // joyride's.
  const handleDismiss = () => {
    closeProps.onClick({
      preventDefault: () => {},
    } as unknown as Parameters<typeof closeProps.onClick>[0]);
  };

  // Authored on UserGuideStep and forwarded by UserGuide via step.data.
  const arrow = (step.data as { arrow?: GuideCardArrow } | undefined)?.arrow;

  return (
    <GuideCard
      {...tooltipProps}
      arrow={arrow ?? "top-left"}
      data-cy="user-guide-tooltip"
      className="w-[430px] max-w-[calc(100vw-32px)]"
      icon={<Icon name="InfoIcon" size="md" />}
      action={
        !isLastStep ? (
          <AlertAction {...primaryProps} data-cy="user-guide-next">
            Next
          </AlertAction>
        ) : undefined
      }
      onDismiss={handleDismiss}
    >
      <AlertTitle>{step.title}</AlertTitle>
      <AlertDescription>{step.content}</AlertDescription>
      {/* Screen-reader progress; visual progress is in the title ("1/2"). */}
      <span className="sr-only">
        Step {index + 1} of {size}
      </span>
    </GuideCard>
  );
};
